import { useState } from 'react'
import { ChevronRightIcon, HourglassIcon } from 'lucide-react'
import type { AccommodationDetail } from '@/api/accommodation/types'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { useIsMobile } from '@/hooks/useIsMobile'

const STEPS = [
  {
    title: 'Pošlete žádost a zaplatíte',
    text: 'Částku jen zablokujeme na kartě, zatím ji nestrhneme.',
  },
  {
    title: 'Hostitel odpoví do 12 hodin',
    text: 'U termínů s blízkým příjezdem dřív. Odpověď vám pošleme e-mailem.',
  },
  {
    title: 'Potvrdí, nebo nepotvrdí',
    text: 'Po potvrzení platí rezervace a částku strhneme. Když hostitel nepotvrdí, blokaci zrušíme a nic neplatíte.',
  },
]

/**
 * Short form of the checkout's ReservationRequestNotice, shown next to the
 * calendar so the guest knows up front that the owner confirms each booking.
 */
export function RequestBookingPanel({ accommodation }: { accommodation: AccommodationDetail }) {
  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()

  if (accommodation.bookingMode !== 'request') {
    return null
  }

  const title = 'Hostitel rezervaci potvrzuje'
  const description =
    'Volné termíny u tohoto ubytování neumíme ověřit automaticky, proto každou rezervaci potvrzuje hostitel.'

  const trigger = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="flex w-full items-center gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-left transition-colors hover:bg-amber-100/60"
    >
      <HourglassIcon className="size-5 shrink-0 text-amber-600" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-800">{title}</div>
        <div className="text-xs text-slate-500">Odpoví do 12 hodin, platbu do té doby jen blokujeme.</div>
      </div>
      <ChevronRightIcon className="size-4 shrink-0 text-amber-700" />
    </button>
  )

  const content = (
    <ol className="space-y-4">
      {STEPS.map((step, index) => (
        <li key={step.title} className="flex gap-3">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-semibold text-amber-700">
            {index + 1}
          </span>
          <div>
            <div className="text-sm font-semibold text-slate-800">{step.title}</div>
            <div className="mt-0.5 text-sm text-slate-500">{step.text}</div>
          </div>
        </li>
      ))}
    </ol>
  )

  if (isMobile) {
    return (
      <>
        {trigger}
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent className="max-h-[90dvh]">
            <DrawerHeader>
              <DrawerTitle>{title}</DrawerTitle>
              <DrawerDescription>{description}</DrawerDescription>
            </DrawerHeader>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">{content}</div>
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <>
      {trigger}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-md">
          <SheetHeader className="border-b border-slate-100 p-6">
            <SheetTitle className="text-xl">{title}</SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto p-6">{content}</div>
        </SheetContent>
      </Sheet>
    </>
  )
}
